import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@mh-wms/api-client';
import { toast } from 'sonner';
import { authedFetch } from '@/shared/api/client';
import { useAuth } from '@/shared/auth/AuthProvider';

export type TableColumnSizes = Record<string, Record<string, number>>;

interface UserPreferencesValue {
  tableColumnSizes: TableColumnSizes;
  saveTableColumnSizes: (tableKey: string, columnSizes: Record<string, number>) => void;
}

const EMPTY_PREFERENCES: UserPreferencesValue = {
  tableColumnSizes: {},
  saveTableColumnSizes: () => undefined,
};

const UserPreferencesContext = createContext<UserPreferencesValue>(EMPTY_PREFERENCES);
const QUERY_KEY = ['current-user', 'preferences'] as const;

export function normalizeColumnSizes(columnSizes: Record<string, number>) {
  return Object.fromEntries(
    Object.entries(columnSizes)
      .filter(([column, width]) => column.length > 0 && Number.isFinite(width))
      .map(([column, width]) => [column, Math.round(Math.min(1200, Math.max(60, width)))]),
  );
}

async function loadPreferences(): Promise<TableColumnSizes> {
  const response = await authedFetch('/api/User/current/preferences');
  if (!response.ok) throw ApiError.from(response, await response.json().catch(() => undefined));
  const result = (await response.json()) as { tableColumnSizes?: TableColumnSizes };
  return result.tableColumnSizes ?? {};
}

async function saveTablePreference(tableKey: string, columnSizes: Record<string, number>) {
  const response = await authedFetch('/api/User/current/preferences/table', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tableKey, columnSizes }),
  });
  if (!response.ok) throw ApiError.from(response, await response.json().catch(() => undefined));
}

export function UserPreferencesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const { data = {} } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: loadPreferences,
    enabled: isAuthenticated,
    staleTime: Infinity,
  });
  const mutation = useMutation({
    mutationFn: ({
      tableKey,
      columnSizes,
    }: {
      tableKey: string;
      columnSizes: Record<string, number>;
    }) => saveTablePreference(tableKey, columnSizes),
    onError: () => toast.error('Column widths could not be saved.'),
  });

  const value = useMemo<UserPreferencesValue>(
    () => ({
      tableColumnSizes: data,
      saveTableColumnSizes(tableKey, columnSizes) {
        queryClient.setQueryData<TableColumnSizes>(QUERY_KEY, (current = {}) => ({
          ...current,
          [tableKey]: columnSizes,
        }));
        mutation.mutate({ tableKey, columnSizes: normalizeColumnSizes(columnSizes) });
      },
    }),
    [data, mutation, queryClient],
  );

  return (
    <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>
  );
}

export function useUserPreferences(): UserPreferencesValue {
  return useContext(UserPreferencesContext);
}
